import { useState } from "react"
import { Pencil, Trash2, Plus, Eye, EyeOff } from "lucide-react"
import { useGetFAQsQuery, useCreateFAQMutation, useUpdateFAQMutation, useDeleteFAQMutation, type AdminFAQ } from "../features/admin/adminApi"

type FormState = { question: string; answer: string; category: string; isPublished: boolean }
const EMPTY: FormState = { question: "", answer: "", category: "", isPublished: true }

export default function FAQsPage() {
  const [filter, setFilter] = useState("")
  const { data: faqs = [], isLoading } = useGetFAQsQuery({ published: filter || undefined })
  const [createFAQ] = useCreateFAQMutation()
  const [updateFAQ] = useUpdateFAQMutation()
  const [deleteFAQ] = useDeleteFAQMutation()

  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<AdminFAQ | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true) }
  const openEdit = (faq: AdminFAQ) => {
    setEditing(faq)
    setForm({ question: faq.question, answer: faq.answer, category: faq.category ?? "", isPublished: faq.isPublished })
    setShowForm(true)
  }
  const closeForm = () => { setShowForm(false); setEditing(null) }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload = { question: form.question, answer: form.answer, category: form.category || undefined, isPublished: form.isPublished }
    if (editing) {
      await updateFAQ({ uid: editing.id, ...payload })
    } else {
      await createFAQ(payload)
    }
    closeForm()
  }

  const handleDelete = async (uid: string) => {
    if (!window.confirm("Delete this FAQ?")) return
    await deleteFAQ(uid)
  }

  const handleTogglePublish = async (faq: AdminFAQ) => {
    await updateFAQ({ uid: faq.id, isPublished: !faq.isPublished })
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">FAQs</h1>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
          <Plus size={16} /> New FAQ
        </button>
      </div>

      <div className="mb-4 flex gap-2">
        <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All</option>
          <option value="true">Published</option>
          <option value="false">Drafts</option>
        </select>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">{editing ? "Edit FAQ" : "New FAQ"}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  placeholder="e.g. Payments, Shipping" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
                <input value={form.question} onChange={(e) => setForm((f) => ({ ...f, question: e.target.value }))}
                  required className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Answer *</label>
                <textarea value={form.answer} onChange={(e) => setForm((f) => ({ ...f, answer: e.target.value }))}
                  required rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm((f) => ({ ...f, isPublished: e.target.checked }))} />
                Published
              </label>
              <div className="flex gap-2 pt-2">
                <button type="submit" className="flex-1 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">{editing ? "Save" : "Create"}</button>
                <button type="button" onClick={closeForm} className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-semibold rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading FAQs...</div>
        ) : faqs.length === 0 ? (
          <div className="p-8 text-center text-gray-400 text-sm">No FAQs found. Click "New FAQ" to create one.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {faqs.map((faq) => (
              <div key={faq.id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {faq.category && <p className="text-xs text-blue-600 font-medium mb-1">{faq.category}</p>}
                    <p className="font-medium text-gray-900">{faq.question}</p>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{faq.answer}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${faq.isPublished ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {faq.isPublished ? "Published" : "Draft"}
                    </span>
                    <button onClick={() => handleTogglePublish(faq)} title={faq.isPublished ? "Unpublish" : "Publish"} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                      {faq.isPublished ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <button onClick={() => openEdit(faq)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => handleDelete(faq.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
